import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import Loader from "@/components/loader";
import SiteLocationMap from "@/components/site-location-map";
import { FieldErrorMessage } from "@/components/stone/form-errors";
import { Button } from "@/components/ui/button";
import { Empty, EmptyDescription } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getFirstErrorMessage } from "@/lib/form-errors";
import { encodePlusCode, tryDecodePlusCode } from "@/lib/plus-codes";
import { trpc } from "@/utils/trpc";
import type {
  AddressSuggestionItem,
  AddressSuggestionsProps,
  SiteSetupFormApi,
} from "./site-setup-types";

const createPlacesSessionToken = () => {
  const cryptoObject = globalThis.crypto;
  if (cryptoObject?.randomUUID) {
    return cryptoObject.randomUUID();
  }
  return Math.random().toString(36).slice(2);
};

function SiteNameField({
  form,
  onFieldInteraction,
}: {
  form: SiteSetupFormApi;
  onFieldInteraction?: () => void;
}) {
  return (
    <form.Field name="siteName">
      {(field) => {
        const fieldError = getFirstErrorMessage(
          field.state.meta.errors as ReadonlyArray<unknown>
        );

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Site Name</Label>
            <Input
              id={field.name}
              name={field.name}
              value={field.state.value}
              aria-invalid={Boolean(fieldError)}
              onBlur={field.handleBlur}
              onChange={(e) => {
                onFieldInteraction?.();
                field.handleChange(e.target.value);
              }}
              placeholder="e.g., Tower Bridge Construction"
            />
            <FieldErrorMessage message={fieldError} />
          </div>
        );
      }}
    </form.Field>
  );
}

function AddressField({
  form,
  onLocationSelect,
  readonly = false,
  onFieldInteraction,
}: {
  form: SiteSetupFormApi;
  onLocationSelect: () => void;
  readonly?: boolean;
  onFieldInteraction?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [sessionToken, setSessionToken] = useState(() =>
    createPlacesSessionToken()
  );
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [pendingSelection, setPendingSelection] =
    useState<AddressSuggestionItem | null>(null);
  const [pendingPlaceId, setPendingPlaceId] = useState<string | null>(null);

  const suggestions = useQuery({
    ...trpc.searchAddress.queryOptions({
      address: query,
      sessionToken,
    }),
    enabled: !manualMode && !readonly && open && query.trim().length >= 3,
  });
  const suggestionItems =
    (suggestions.data as AddressSuggestionItem[] | undefined) ?? [];
  const resolvePlaceQuery = useQuery({
    ...trpc.resolvePlace.queryOptions({
      placeId: pendingPlaceId ?? "",
      sessionToken,
    }),
    enabled: Boolean(pendingPlaceId),
    retry: false,
  });

  useEffect(() => {
    if (!pendingSelection || !resolvePlaceQuery.data) {
      return;
    }
    const resolved = resolvePlaceQuery.data;
    const formatted = resolved.formattedAddress || pendingSelection.description;
    form.setFieldValue("address", formatted);
    form.setFieldValue("lat", resolved.latitude);
    form.setFieldValue("lon", resolved.longitude);
    const nextPlusCode =
      resolved.plusCode ??
      encodePlusCode(resolved.latitude, resolved.longitude);
    form.setFieldValue("plusCode", nextPlusCode);
    setOpen(false);
    setSelectedIndex(null);
    setQuery("");
    setSessionToken(createPlacesSessionToken());
    setPendingSelection(null);
    setPendingPlaceId(null);
    onLocationSelect();
  }, [form, onLocationSelect, pendingSelection, resolvePlaceQuery.data]);

  useEffect(() => {
    if (!resolvePlaceQuery.error) return;
    setSuggestionError(
      resolvePlaceQuery.error.message ||
        "Unable to resolve the address right now."
    );
    setPendingSelection(null);
    setPendingPlaceId(null);
  }, [resolvePlaceQuery.error]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      setSelectedIndex(null);
      setPendingSelection(null);
      setPendingPlaceId(null);
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open]);

  return (
    <form.Field name="address">
      {(field) => {
        const fieldError = getFirstErrorMessage(
          field.state.meta.errors as ReadonlyArray<unknown>
        );

        const handleSuggestionSelect = (item: AddressSuggestionItem) => {
          setSuggestionError(null);
          setPendingSelection(item);
          setPendingPlaceId(item.placeId);
        };

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Address</Label>
            <div className="relative">
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value}
                aria-invalid={Boolean(fieldError)}
                onChange={(e) => {
                  if (readonly) return;
                  onFieldInteraction?.();
                  setSuggestionError(null);
                  setSelectedIndex(null);
                  const value = e.target.value;
                  form.setFieldValue("lat", null);
                  form.setFieldValue("lon", null);
                  form.setFieldValue("plusCode", "");
                  field.handleChange(value);
                  const trimmed = value.trim();
                  setQuery(trimmed);
                  setOpen(trimmed.length >= 3);
                }}
                placeholder={readonly ? "" : "Search an address"}
                autoComplete="off"
                spellCheck={false}
                disabled={manualMode || readonly}
                readOnly={readonly}
              />
              {!readonly && !manualMode && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  {(suggestions.isFetching || resolvePlaceQuery.isFetching) && (
                    <Loader
                      size="xxs"
                      layout="inline"
                      showLabel={false}
                      className="pt-0"
                    />
                  )}
                </div>
              )}
            </div>
            {!readonly && (
              <div className="mt-1 text-xs">
                <button
                  type="button"
                  className="underline text-muted-foreground hover:text-foreground"
                  onClick={() => {
                    setManualMode((v) => {
                      const next = !v;
                      if (next) {
                        setOpen(false);
                      } else {
                        const trimmed = (field.state.value || "").trim();
                        setQuery(trimmed);
                        setOpen(trimmed.length >= 3);
                      }
                      return next;
                    });
                    setSuggestionError(null);
                  }}
                >
                  {manualMode
                    ? "Use address search instead"
                    : "Enter coordinates manually"}
                </button>
              </div>
            )}

            {!readonly && !manualMode && open && query.length >= 3 && (
              <div className="relative">
                <div className="absolute z-50 w-full">
                  {suggestions.isError ? (
                    <div
                      role="alert"
                      aria-live="polite"
                      className="rounded-md border border-destructive/25 bg-destructive/10 p-2 text-sm text-destructive"
                    >
                      Unable to search address right now.
                    </div>
                  ) : (
                    <AddressSuggestions
                      key={query}
                      items={suggestionItems}
                      selectedIndex={selectedIndex}
                      setSelectedIndex={setSelectedIndex}
                      onSelect={(item) => {
                        if (resolvePlaceQuery.isFetching || pendingPlaceId)
                          return;
                        setSelectedIndex(
                          suggestionItems.findIndex(
                            (option) => option.placeId === item.placeId
                          )
                        );
                        handleSuggestionSelect(item);
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            <FieldErrorMessage message={suggestionError} />
            {!readonly && manualMode && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <form.Field name="lat">
                  {(latField) => (
                    <div className="space-y-1">
                      <Label htmlFor="lat">Latitude</Label>
                      <Input
                        id="lat"
                        name="lat"
                        type="number"
                        step="any"
                        value={latField.state.value ?? ""}
                        onChange={(e) => {
                          onFieldInteraction?.();
                          const v = e.target.value;
                          latField.handleChange(v === "" ? null : Number(v));
                          onLocationSelect();
                        }}
                        placeholder="e.g., 51.5074"
                        autoComplete="off"
                      />
                    </div>
                  )}
                </form.Field>
                <form.Field name="lon">
                  {(lonField) => (
                    <div className="space-y-1">
                      <Label htmlFor="lon">Longitude</Label>
                      <Input
                        id="lon"
                        name="lon"
                        type="number"
                        step="any"
                        value={lonField.state.value ?? ""}
                        onChange={(e) => {
                          onFieldInteraction?.();
                          const v = e.target.value;
                          lonField.handleChange(v === "" ? null : Number(v));
                          onLocationSelect();
                        }}
                        placeholder="e.g., -0.1278"
                        autoComplete="off"
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            )}
            <FieldErrorMessage message={fieldError} />
          </div>
        );
      }}
    </form.Field>
  );
}

function AddressSuggestions({
  items,
  selectedIndex,
  setSelectedIndex,
  onSelect,
}: AddressSuggestionsProps) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-md border bg-popover shadow-lg">
        <Empty>
          <EmptyDescription>
            No results. Try refining the address.
          </EmptyDescription>
        </Empty>
        <div className="px-3 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground text-right border-t">
          Powered by Google
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-md border bg-popover shadow-lg overflow-hidden">
      <ul className="divide-y max-h-[260px] overflow-y-auto">
        {items.map((s, i) => (
          <li
            key={s.placeId}
            className={`p-2 text-sm cursor-pointer ${
              selectedIndex === i ? "bg-accent" : "hover:bg-muted/60"
            }`}
            onClick={() => {
              setSelectedIndex(i);
              onSelect(s);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                setSelectedIndex(i);
                onSelect(s);
              }
            }}
          >
            <div className="font-medium text-foreground">{s.primaryText}</div>
            {s.secondaryText ? (
              <div className="text-xs text-muted-foreground mt-1">
                {s.secondaryText}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      <div className="px-3 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground text-right bg-popover">
        Powered by Google
      </div>
    </div>
  );
}

function PlusCodeField({
  form,
  onManualEditChange,
}: {
  form: SiteSetupFormApi;
  onManualEditChange: (editing: boolean) => void;
}) {
  const [error, setError] = useState<string | null>(null);

  const applyPlusCode = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) {
        setError(null);
        form.setFieldValue("plusCode", "");
        onManualEditChange(false);
        return;
      }
      const decoded = tryDecodePlusCode(trimmed);
      if (!decoded) {
        setError("Enter a full Plus Code (e.g., 9C3XGV3C+XF)");
        return;
      }
      const normalized = encodePlusCode(
        decoded.latitudeCenter,
        decoded.longitudeCenter
      );
      form.setFieldValue("plusCode", normalized);
      form.setFieldValue("lat", decoded.latitudeCenter);
      form.setFieldValue("lon", decoded.longitudeCenter);
      setError(null);
      onManualEditChange(false);
    },
    [form, onManualEditChange]
  );

  return (
    <form.Field name="plusCode">
      {(field) => {
        const value = field.state.value ?? "";
        const latField = form.getFieldValue("lat");
        const lonField = form.getFieldValue("lon");
        const hasCoordinates =
          typeof latField === "number" &&
          Number.isFinite(latField) &&
          typeof lonField === "number" &&
          Number.isFinite(lonField);

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Plus Code</Label>
            <div className="flex gap-2">
              <Input
                id={field.name}
                name={field.name}
                value={value}
                placeholder="e.g., 9C3XGV3C+XF"
                autoComplete="off"
                spellCheck={false}
                onFocus={() => {
                  onManualEditChange(true);
                  setError(null);
                }}
                onChange={(event) => {
                  onManualEditChange(true);
                  setError(null);
                  field.handleChange(event.target.value);
                }}
                onBlur={(event) => {
                  field.handleBlur();
                  applyPlusCode(event.target.value);
                }}
              />
              <Button
                type="button"
                variant="outline"
                className="shrink-0"
                onClick={() => applyPlusCode(value)}
                disabled={!value.trim()}
              >
                Use
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Share this with crews for precise entrance directions.
            </p>
            <FieldErrorMessage message={error} />
            {!error && !value && hasCoordinates ? (
              <p className="text-xs text-muted-foreground">
                A Plus Code will be generated automatically when you position
                the marker.
              </p>
            ) : null}
          </div>
        );
      }}
    </form.Field>
  );
}

function LocationMapField({
  form,
  onLocationChange,
  onFieldInteraction,
}: {
  form: SiteSetupFormApi;
  onLocationChange: () => void;
  onFieldInteraction?: () => void;
}) {
  const queryClient = useQueryClient();

  const handlePositionChange = useCallback(
    async (next: { lat: number; lon: number }) => {
      onFieldInteraction?.();
      form.setFieldValue("lat", next.lat);
      form.setFieldValue("lon", next.lon);
      const normalized = encodePlusCode(next.lat, next.lon);
      form.setFieldValue("plusCode", normalized);
      onLocationChange();

      try {
        const result = await queryClient.fetchQuery(
          trpc.reverseGeocode.queryOptions({
            lat: next.lat,
            lon: next.lon,
          })
        );

        if (result?.address) {
          form.setFieldValue("address", result.address);
        }
      } catch (error) {
        console.error("Reverse geocode lookup failed:", error);
      }
    },
    [form, onFieldInteraction, onLocationChange, queryClient]
  );

  return (
    <form.Subscribe selector={(state) => [state.values.lat, state.values.lon]}>
      {([lat, lon]) => {
        const hasPosition =
          typeof lat === "number" &&
          Number.isFinite(lat) &&
          typeof lon === "number" &&
          Number.isFinite(lon);

        const defaultLat = 51.505; // London
        const defaultLon = -0.09;
        const effectiveLat = hasPosition ? (lat as number) : defaultLat;
        const effectiveLon = hasPosition ? (lon as number) : defaultLon;

        return (
          <div className="space-y-2">
            <Label>Location</Label>
            <form.Field name="geofenceDistance">
              {(field) => (
                <LocationMapWithRadius
                  lat={effectiveLat}
                  lon={effectiveLon}
                  geofenceDistance={field.state.value}
                  onPositionChange={handlePositionChange}
                />
              )}
            </form.Field>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

function LocationMapWithRadius({
  lat,
  lon,
  geofenceDistance,
  onPositionChange,
}: {
  lat: number;
  lon: number;
  geofenceDistance: string;
  onPositionChange: (coords: { lat: number; lon: number }) => void;
}) {
  const radius = useMemo(() => {
    const parsed = Number(geofenceDistance);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 100;
  }, [geofenceDistance]);

  return (
    <SiteLocationMap
      lat={lat}
      lon={lon}
      radius={radius}
      onPositionChange={onPositionChange}
    />
  );
}

function GeofenceDistanceField({
  form,
  helperText,
  onFieldInteraction,
}: {
  form: SiteSetupFormApi;
  helperText?: string;
  onFieldInteraction?: () => void;
}) {
  return (
    <form.Field name="geofenceDistance">
      {(field) => {
        const fieldError = getFirstErrorMessage(
          field.state.meta.errors as ReadonlyArray<unknown>
        );
        const options = [
          { value: "50", label: "50 meters" },
          { value: "100", label: "100 meters" },
          { value: "200", label: "200 meters" },
          { value: "500", label: "500 meters" },
          { value: "1000", label: "1000 meters" },
        ];

        return (
          <div className="space-y-2">
            <Label htmlFor={field.name}>Geofence Distance</Label>
            <Select
              value={field.state.value}
              onValueChange={(value) => {
                onFieldInteraction?.();
                field.handleChange(value);
              }}
            >
              <SelectTrigger
                className="w-full"
                aria-invalid={Boolean(fieldError)}
              >
                <SelectValue placeholder="Select distance" />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {helperText ? (
              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <p className="text-muted-foreground text-xs">{helperText}</p>
              </div>
            ) : null}
            <FieldErrorMessage message={fieldError} />
          </div>
        );
      }}
    </form.Field>
  );
}

export {
  SiteNameField,
  AddressField,
  AddressSuggestions,
  PlusCodeField,
  LocationMapField,
  LocationMapWithRadius,
  GeofenceDistanceField,
};
